/**
 * Screenshots Feature — Mock Data
 *
 * Realistic mock data feeding the BehaviorSubject in ScreenshotsService while the
 * backend endpoints don't exist yet. Every consumer of this data is marked with
 * `// MOCK-DATA:` so we can grep all replacement sites when the API ships.
 *
 * TODO(API): GET /api/v1/takeoffs/:takeoffId/screenshots — payload shape: Screenshot[]
 */

import { Screenshot, Contact } from './screenshots.types';

/** Inline 1x1 transparent PNG placeholder so cards render without a network fetch. */
const PLACEHOLDER_THUMB =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="280" height="180" viewBox="0 0 280 180">
      <rect width="280" height="180" fill="#F3F4F6"/>
      <rect x="20" y="20" width="240" height="140" fill="#E5E7EB" stroke="#D1D5DB" stroke-width="2"/>
      <text x="140" y="95" font-family="Arial" font-size="14" fill="#6B7280" text-anchor="middle">Mock Screenshot</text>
    </svg>`
  );

const nowIso = () => new Date().toISOString();
const minutesAgo = (m: number) => new Date(Date.now() - m * 60_000).toISOString();
const hoursAgo = (h: number) => new Date(Date.now() - h * 3_600_000).toISOString();
const daysAgo = (d: number) => new Date(Date.now() - d * 86_400_000).toISOString();

// MOCK-DATA: replace with API GET /api/v1/takeoffs/:takeoffId/screenshots when ready
export const MOCK_SCREENSHOTS: Screenshot[] = [
  {
    id: 'ss-1001',
    pageId: '1',
    pageName: 'A1.0 — Cover Sheet',
    region: { x: 120, y: 80, width: 320, height: 220 },
    thumbnail: PLACEHOLDER_THUMB,
    caption: 'Title block and project info',
    createdAt: minutesAgo(8),
    annotations: [],
    shares: [],
  },
  {
    id: 'ss-1002',
    pageId: '2',
    pageName: 'A2.1 — Floor Plan Level 1',
    region: { x: 340, y: 180, width: 480, height: 360 },
    thumbnail: PLACEHOLDER_THUMB,
    caption: 'East wing corridor — width check',
    createdAt: minutesAgo(22),
    linkedMeasurementId: 'm-corridor-east',
    linkedMeasurementName: 'East Corridor Length',
    linkedMeasurementColor: '#3B82F6',
    annotations: [
      { id: 'a-1', kind: 'arrow', color: '#FF6600', x: 40, y: 60, endX: 220, endY: 60, strokeWidth: 3 },
      { id: 'a-2', kind: 'text', color: '#FF6600', x: 110, y: 40, text: 'Verify w/ structural', strokeWidth: 1 },
    ],
    shares: [],
  },
  {
    id: 'ss-1003',
    pageId: '2',
    pageName: 'A2.1 — Floor Plan Level 1',
    region: { x: 60, y: 420, width: 260, height: 180 },
    thumbnail: PLACEHOLDER_THUMB,
    caption: 'Restroom group — fixture count',
    createdAt: hoursAgo(1),
    linkedMeasurementId: 'm-restroom-count',
    linkedMeasurementName: 'Restroom Fixtures',
    linkedMeasurementColor: '#10B981',
    annotations: [
      { id: 'a-3', kind: 'rect', color: '#10B981', x: 30, y: 30, width: 180, height: 100, strokeWidth: 3 },
    ],
    shares: [
      {
        id: 'sh-1',
        channel: 'email',
        recipients: [
          { id: 'c-3', name: 'Maria Lopez', email: 'maria@acme.com', role: 'PM', company: 'Acme' },
        ],
        access: 'restricted',
        expiration: '7d',
        allowDownload: true,
        allowComments: true,
        requireResponse: false,
        includeAnnotations: true,
        includeLinkedMeasurement: true,
        shareUrl: 'https://takeoff.app/s/ss-1003-sh-1',
        viewCount: 2,
        status: 'viewed',
        createdAt: hoursAgo(0.5),
      },
    ],
  },
  {
    id: 'ss-1004',
    pageId: '3',
    pageName: 'A3.2 — North Elevation',
    region: { x: 220, y: 110, width: 540, height: 240 },
    thumbnail: PLACEHOLDER_THUMB,
    caption: 'Curtain wall mullion spacing',
    createdAt: hoursAgo(3),
    annotations: [],
    shares: [],
  },
  {
    id: 'ss-1005',
    pageId: '3',
    pageName: 'A3.2 — North Elevation',
    region: { x: 80, y: 360, width: 340, height: 200 },
    thumbnail: PLACEHOLDER_THUMB,
    caption: 'Storefront entry detail',
    createdAt: hoursAgo(5),
    annotations: [
      { id: 'a-4', kind: 'highlight', color: '#F59E0B', x: 20, y: 80, width: 220, height: 40, strokeWidth: 14 },
    ],
    shares: [],
  },
  {
    id: 'ss-1006',
    pageId: '4',
    pageName: 'S2.0 — Foundation Plan',
    region: { x: 150, y: 250, width: 420, height: 280 },
    thumbnail: PLACEHOLDER_THUMB,
    caption: 'Footing F3 — rebar callout',
    createdAt: daysAgo(1),
    linkedMeasurementId: 'm-footing-f3',
    linkedMeasurementName: 'Footing F3 Area',
    linkedMeasurementColor: '#EF4444',
    annotations: [
      { id: 'a-5', kind: 'pen', color: '#EF4444', x: 40, y: 40, points: [
        { x: 40, y: 40 }, { x: 70, y: 55 }, { x: 110, y: 80 }, { x: 150, y: 100 },
      ], strokeWidth: 4 },
    ],
    shares: [],
  },
  {
    id: 'ss-1007',
    pageId: '4',
    pageName: 'S2.0 — Foundation Plan',
    region: { x: 600, y: 80, width: 200, height: 320 },
    thumbnail: PLACEHOLDER_THUMB,
    caption: 'Column grid C-D verification',
    createdAt: daysAgo(1),
    annotations: [],
    shares: [],
  },
  {
    id: 'ss-1008',
    pageId: '5',
    pageName: 'M1.1 — HVAC Plan',
    region: { x: 200, y: 200, width: 380, height: 240 },
    thumbnail: PLACEHOLDER_THUMB,
    caption: 'VAV box layout — zone 4',
    createdAt: daysAgo(2),
    annotations: [],
    shares: [],
  },
  {
    id: 'ss-1009',
    pageId: '5',
    pageName: 'M1.1 — HVAC Plan',
    region: { x: 50, y: 480, width: 280, height: 160 },
    thumbnail: PLACEHOLDER_THUMB,
    caption: 'Roof unit access detail',
    createdAt: daysAgo(2),
    annotations: [
      { id: 'a-6', kind: 'arrow', color: '#3B82F6', x: 30, y: 40, endX: 180, endY: 80, strokeWidth: 3 },
    ],
    shares: [
      {
        id: 'sh-2',
        channel: 'link',
        recipients: [],
        access: 'anyone',
        expiration: '30d',
        allowDownload: false,
        allowComments: true,
        requireResponse: false,
        includeAnnotations: true,
        includeLinkedMeasurement: false,
        shareUrl: 'https://takeoff.app/s/ss-1009-sh-2',
        viewCount: 5,
        status: 'viewed',
        createdAt: daysAgo(2),
      },
    ],
  },
  {
    id: 'ss-1010',
    pageId: '6',
    pageName: 'E1.0 — Electrical Plan',
    region: { x: 100, y: 100, width: 600, height: 380 },
    thumbnail: PLACEHOLDER_THUMB,
    caption: 'Panel schedule overview',
    createdAt: daysAgo(3),
    annotations: [],
    shares: [],
  },
];

// MOCK-DATA: replace with API GET /api/v1/contacts/search?q=... when ready
export const MOCK_CONTACTS: Contact[] = [
  { id: 'c-1', name: 'Joe Anderson', email: 'joe.anderson@acme.com', role: 'Architect', company: 'Acme Architects', color: '#FF6600' },
  { id: 'c-2', name: 'Joe Bautista', email: 'joe.bautista@summit-eng.com', role: 'Structural Engineer', company: 'Summit Engineering', color: '#3B82F6' },
  { id: 'c-3', name: 'Maria Lopez', email: 'maria@acme.com', role: 'Project Manager', company: 'Acme Architects', color: '#10B981' },
  { id: 'c-4', name: 'David Chen', email: 'dchen@megabuilders.com', role: 'GC Superintendent', company: 'Mega Builders', color: '#EC4899' },
  { id: 'c-5', name: 'Sarah Williams', email: 'sarah.w@mep-corp.com', role: 'MEP Engineer', company: 'MEP Corp', color: '#8B5CF6' },
  { id: 'c-6', name: 'Tom Reeves', email: 'tom@reevesglass.com', role: 'Glazing Sub', company: 'Reeves Glass', color: '#F59E0B' },
  { id: 'c-7', name: 'Priya Patel', email: 'priya@civil-design.com', role: 'Civil Engineer', company: 'Civil Design Inc', color: '#EF4444' },
  { id: 'c-8', name: 'Mike O\'Brien', email: 'mike@obrienroof.com', role: 'Roofing Sub', company: "O'Brien Roofing", color: '#111827' },
  { id: 'c-9', name: 'Lisa Tan', email: 'lisa.tan@acme.com', role: 'Owner Rep', company: 'Acme Architects', color: '#3B82F6' },
  { id: 'c-10', name: 'Carlos Diaz', email: 'cdiaz@steelfab.com', role: 'Steel Fabricator', company: 'SteelFab', color: '#10B981' },
  { id: 'c-11', name: 'Anna Kowalski', email: 'anna@hvac-pro.com', role: 'HVAC Lead', company: 'HVAC Pro', color: '#EC4899' },
  { id: 'c-12', name: 'James Park', email: 'jpark@elec-systems.com', role: 'Electrical Engineer', company: 'ELEC Systems', color: '#8B5CF6' },
];
